import { useState } from 'react';
import { DatePicker } from '@g2u/ui-kit';
import { Form } from 'antd';

import { Section } from 'components';
import { format, isAfter, set, setHours } from 'date-fns';
import { useTechnicianAvailability } from 'hooks/useTechnicianAvailability';

// patch for types missing from ui-kit
import type { PickerProps } from 'antd/es/date-picker/generatePicker';
type OnPanelChange = PickerProps<Date>['onPanelChange'];

type Props = { technicianId: number; setSlot: ({ day, time }: { day: string; time: string }) => void };

export const SelectSlotSimple = ({ setSlot, technicianId }: Props) => {
  const [pickerValue, setPickerValue] = useState(new Date());
  const handleSlotChange = (date: Date) => {
    setSlot({ day: format(date, 'dd-MM-yyyy'), time: format(date, 'HH:mm:ss') });
  };

  const [availability] = useTechnicianAvailability(technicianId, format(pickerValue, 'yyyy-MM-01'));

  const unavailableSlots = availability
    ? Object.fromEntries(
        Object.keys(availability)
          .map(
            (date) =>
              [
                date,
                Object.entries(availability[date])
                  .filter(([_, { status }]) => status === 'J')
                  .map(([time]) => time),
              ] as [string, string[]],
          )
          .filter(([_, slots]) => slots.length > 0),
      )
    : {};

  const isUnavailable = (date: Date) =>
    unavailableSlots?.[format(date, 'yyyy-MM-dd')]?.includes(format(date, 'HH:mm:ss')) ||
    !availability?.[format(date, 'yyyy-MM-dd')]?.[format(date, 'HH:mm:ss')];

  return (
    <Section title="Select Date and Time" loading={!availability}>
      <Form layout="vertical">
        <DatePicker.$
          name="slot"
          onSelect={handleSlotChange}
          onPanelChange={((value, mode) => mode === 'date' && setPickerValue(value) && true) as OnPanelChange}
          pickerValue={pickerValue}
          // @ts-expect-error ts(2322) Type '*' is not assignable to type 'boolean | undefined'
          showTime={{ minuteStep: 60, needConfirm: true, hideDisabledOptions: true }}
          disabledDate={(current) => isAfter(current!, new Date())}
          disabledTime={(current) => ({
            disabledHours: () =>
              [...Array(24).keys()].filter(
                (hour) =>
                  current &&
                  (isAfter(setHours(current, hour), new Date()) ||
                    isUnavailable(set(current, { hours: hour, minutes: 0, seconds: 0 })) ||
                    isUnavailable(set(current, { hours: hour, minutes: 30, seconds: 0 }))),
              ),
          })}
          maxDate={new Date()}
          format="yyyy-MM-dd H:mm"
          label="Please select a previous date and time:"
          colon={false}
        />
      </Form>
    </Section>
  );
};
